import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import MuiListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Tooltip from '@material-ui/core/Tooltip'
import ExpandLess from 'components/material-symbol/icons/ExpandLess'
import ExpandMore from 'components/material-symbol/icons/ExpandMore'
import List from '@material-ui/core/List'
import Collapse from '@material-ui/core/Collapse'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import { useIsTablet } from 'lib/hooks'
import { NavLink, useLocation } from 'react-router-dom'
import { useGetCurrentPrograms } from './api-hooks'
import { PROGRAM_ROUTES } from 'lib/junior-league-utils/program-categories'

const listItemStyles = theme => ({
  root: {
    color: theme.palette.sidebar.itemText,
    borderRadius: theme.palette.sidebar.itemBorderRadius,
    marginLeft: 12,
    marginRight: 12,
    width: 'calc(100% - 24px)',
    paddingLeft: 16,
    paddingRight: 24,
    minHeight: 56,
    transition: theme.transitions.create(
      ['background-color', 'background-image'],
      { duration: theme.transitions.duration.shortest },
    ),
    '&:hover': {
      backgroundColor: theme.palette.sidebar.hoverOverlay,
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.focusOverlay,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.sidebar.activeBg,
      color: theme.palette.sidebar.itemText,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.sidebar.activeBg,
    },
    '&.Mui-selected.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.activeBg,
      backgroundImage: `linear-gradient(${theme.palette.sidebar.focusOverlay}, ${theme.palette.sidebar.focusOverlay})`,
    },
    '& .MuiListItemText-root': {
      margin: 0,
      minWidth: 0,
      overflow: 'hidden',
    },
  },
})
const ListItem = withStyles(listItemStyles)(MuiListItem)

const programListItemTextProps = name => ({
  style: { fontWeight: 500 },
  noWrap: true,
  title: name,
})

const ICON_CLASS = 'JrlProgramsSubMenu-icon'

const useSubMenuStyles = makeStyles({
  collapsedItem: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 4,
    paddingRight: 4,
    marginLeft: 4,
    marginRight: 4,
    width: 'calc(100% - 8px)',
    minHeight: 48,
  },
  iconWrapper: {
    color: 'inherit',
    minWidth: 40,
    [`& .${ICON_CLASS}`]: {
      height: 24,
      width: 24,
    },
  },
  iconWrapperCollapsed: {
    minWidth: 0,
    justifyContent: 'center',
  },
})

export const ProgramsSubMenu = ({
  onClick,
  isTabletNavOpen,
  setIsTabletNavOpen,
  collapsed = false,
  icon,
  label = 'Current Programs',
}) => {
  const { pathname } = useLocation()
  const { data: programs, loading } = useGetCurrentPrograms()
  const filteredPrograms = programs?.filter(
    program =>
      program.year >= new Date().getFullYear() || program.year === null,
  )
  const isTablet = useIsTablet()
  const classes = useSubMenuStyles()
  const iconComponent = icon
    ? React.cloneElement(icon, { className: ICON_CLASS })
    : null

  const [open, setOpen] = useState(false)
  const handleExpand = () => setOpen(s => !s)

  const handleNavbarExpand = () => setIsTabletNavOpen(s => !s)

  useEffect(() => {
    if (!loading) {
      setOpen(true)
    }
  }, [loading, setOpen])

  const headerItem = (
    <ListItem
      button
      onClick={isTablet ? handleNavbarExpand : handleExpand}
      disabled={loading || filteredPrograms?.length === 0}
      className={clsx({ [classes.collapsedItem]: collapsed })}
    >
      {collapsed && iconComponent ? (
        <ListItemIcon
          className={clsx(classes.iconWrapper, classes.iconWrapperCollapsed)}
        >
          {iconComponent}
        </ListItemIcon>
      ) : (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              style: { fontWeight: 500 },
              noWrap: true,
            }}
          />
          {open ? (
            <ExpandLess
              style={{ display: !isTabletNavOpen ? 'none' : 'block' }}
            />
          ) : (
            <ExpandMore />
          )}
        </>
      )}
    </ListItem>
  )

  return (
    <>
      {collapsed ? (
        <Tooltip title={label} placement="right" enterDelay={200}>
          {headerItem}
        </Tooltip>
      ) : (
        headerItem
      )}
      {isTabletNavOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {(filteredPrograms || []).map(program => (
              <ListItem
                key={program.id}
                button
                onClick={onClick}
                selected={pathname.includes(program.id)}
                component={NavLink}
                to={PROGRAM_ROUTES[program.category].program(
                  program.programLocationId,
                  program.id,
                )}
              >
                <ListItemText
                  primary={program.name}
                  primaryTypographyProps={programListItemTextProps(
                    program.name,
                  )}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}
